import { supabase } from '@/integrations/supabase/client';
import {
  getFirstTouchAcquisitionContext,
  type AcquisitionContext,
} from '@/utils/acquisitionAttribution';

// Row shape shared with the persist-user-acquisition Edge Function and the
// signInWithOtp metadata snapshot. Server-side the function fill-empty
// merges matching-touch enrichment into user_acquisition. An unrelated later
// campaign cannot fill gaps in the original account acquisition row.
export interface UserAcquisitionPayload {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer: string | null;
  referrer_source: string | null;
  landing_page: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  fbclid: string | null;
  fbc: string | null;
  ttclid: string | null;
  msclkid: string | null;
  af_status: string | null;
}

function truncateNullable(value: string | null | undefined, maxLength: number): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.length > maxLength ? trimmed.slice(0, maxLength) : trimmed;
}

function isSyntheticNativeCallback(acquisition: AcquisitionContext): boolean {
  if (!acquisition.source_url) return false;
  try {
    const url = new URL(acquisition.source_url);
    return url.protocol === 'appsflyer:' && url.hostname === 'native-attribution' &&
      !url.username && !url.password && !url.port && (url.pathname === '' || url.pathname === '/');
  } catch {
    return false;
  }
}

export function buildUserAcquisitionPayload(
  acquisition: AcquisitionContext | null
): UserAcquisitionPayload | null {
  if (!acquisition) return null;

  const payload: UserAcquisitionPayload = {
    utm_source: truncateNullable(acquisition.utm_source || acquisition.network, 100),
    utm_medium: truncateNullable(acquisition.utm_medium, 100),
    utm_campaign: truncateNullable(acquisition.utm_campaign || acquisition.campaign, 100),
    utm_content: truncateNullable(acquisition.utm_content || acquisition.creative, 100),
    utm_term: truncateNullable(acquisition.utm_term || acquisition.keyword, 100),
    referrer: truncateNullable(acquisition.referrer, 500),
    referrer_source: truncateNullable(acquisition.referrer_source, 20),
    landing_page: truncateNullable(acquisition.landing_path || acquisition.source_url, 500),
    gclid: truncateNullable(acquisition.gclid, 200),
    gbraid: truncateNullable(acquisition.gbraid, 200),
    wbraid: truncateNullable(acquisition.wbraid, 200),
    fbclid: truncateNullable(acquisition.fbclid, 300),
    fbc: truncateNullable(acquisition.fbc, 350),
    ttclid: truncateNullable(acquisition.ttclid, 200),
    msclkid: truncateNullable(acquisition.msclkid, 200),
    af_status: truncateNullable(acquisition.af_status, 50),
  };

  // A native routing callback can precede conversion attribution. Its landing
  // and transport metadata alone must not finalize the account's first touch.
  if (isSyntheticNativeCallback(acquisition) && Object.entries(payload).every(
    ([key, value]) => key === 'landing_page' || key === 'referrer_source' || value === null
  )) return null;

  const hasSignal = Object.entries(payload).some(
    ([key, value]) => key !== 'referrer_source' && value !== null
  );
  return hasSignal ? payload : null;
}

// The first-touch fields track-event attaches to the server-side PostHog
// sign_up (supabase/functions/track-event/signupAttribution.ts). Same values
// as the account's user_acquisition row, without click ids, and with the
// landing page and referrer cut before any query or fragment (the UTMs have
// their own fields; a query can hold click ids).
export type SignupAttributionPayload = Pick<
  UserAcquisitionPayload,
  | 'landing_page'
  | 'referrer'
  | 'referrer_source'
  | 'utm_source'
  | 'utm_medium'
  | 'utm_campaign'
  | 'utm_term'
  | 'utm_content'
>;

function withoutQueryOrFragment(value: string | null): string | null {
  if (!value) return null;
  const cut = value.search(/[?#]/);
  const kept = (cut === -1 ? value : value.slice(0, cut)).trim();
  return kept || null;
}

export function buildSignupAttributionPayload(): SignupAttributionPayload | null {
  const payload = buildUserAcquisitionPayload(getFirstTouchAcquisitionContext());
  if (!payload) return null;
  return {
    landing_page: withoutQueryOrFragment(payload.landing_page),
    referrer: withoutQueryOrFragment(payload.referrer),
    referrer_source: payload.referrer_source,
    utm_source: payload.utm_source,
    utm_medium: payload.utm_medium,
    utm_campaign: payload.utm_campaign,
    utm_term: payload.utm_term,
    utm_content: payload.utm_content,
  };
}

// Spread into signInWithOtp options so magic-link signups carry their
// acquisition snapshot in raw_user_meta_data. The confirming browser is
// usually a different one (mail app webview) with no localStorage context;
// the persist-user-acquisition function falls back to this snapshot.
export function buildSignupAcquisitionMetadata(): { data: Record<string, unknown> } | Record<string, never> {
  const payload = buildUserAcquisitionPayload(getFirstTouchAcquisitionContext());
  if (!payload) return {};
  return { data: { jp_acquisition: payload } };
}

// Sends the first observed acquisition context to the server. Returns true when the
// server confirms a row exists for this user (even one persisted earlier or
// built from signup metadata), false when the caller should retry later.
export async function persistUserAcquisitionToServer(): Promise<boolean> {
  const acquisition = getFirstTouchAcquisitionContext();
  const payload = buildUserAcquisitionPayload(acquisition);
  // Sending null would still permit signup-metadata fallback to create a row.
  // Keep the pending retry until this local native touch has attribution.
  if (acquisition && !payload && isSyntheticNativeCallback(acquisition)) return false;

  try {
    const { data, error } = await supabase.functions.invoke<{ persisted: boolean }>(
      'persist-user-acquisition',
      { body: { acquisition: payload } }
    );

    if (error) {
      console.error('Error persisting user acquisition:', error);
      return false;
    }

    return data?.persisted === true;
  } catch (error) {
    console.error('Error persisting user acquisition:', error);
    return false;
  }
}
